import type { CSSProperties } from 'react'

import { cx } from '../atoms/cx.ts'
import { hasSkin } from '../tokens/skin.ts'

import {
  ARM_BASE_X as BASE_X,
  ARM_BASE_Y as BASE_Y,
  ARM_L1 as L1,
  ARM_L2 as L2,
  ARM_L3 as L3,
  ARM_SHOULDER_X as SHOULDER_X,
  ARM_SHOULDER_Y as SHOULDER_Y,
  gripperTip,
} from './armKinematics.ts'

import './figures.css'

/**
 * 6축 협동로봇 팔 — 와이어프레임 `armSVGInner()` 1155행.
 *
 * 관절값 J1~J6 로 그려지는 SVG다. 링크 길이·베이스 좌표는 실물 상수 그대로(1143행).
 * 🔴 **마디가 회전 묶음(`<g>`)으로 이어져 있다**(2026-10-07 · eric 「움직임을 줘」) — 어깨 · 팔꿈치 · 손목 묶음이 각자 제 관절점을
 *    축으로 CSS `transform: rotate()` 로 돌아가므로, 관절값이 바뀌면 **토큰 시간(`--dur-move`)만큼 부드럽게 옮겨 간다.**
 *    절대 각은 종전 계산과 같다(어깨 J2 → 팔꿈치 +J3 → 손목 +J4+180°) — 모양이 바뀐 것이 아니라 «옮겨 가는 길»이 생긴 것이다.
 * ⚠ 관절값의 출처는 부르는 쪽이 정한다 — 실측값이든(`jointValues`) 도식 자세든(`ROBOT_POSES`) 그림은 받아 그리기만 한다.
 */
export interface ArmFigureProps {
  /** J1~J6(도). 길이 6 미만이면 나머지는 0 으로 본다 */
  joints: readonly number[]
  holding?: boolean
  /** 관절 이름표(J1~J4)를 그릴지. 제어 화면은 끈다 */
  jointLabels?: boolean
  label: string
  /** 이동 중 — 팔 전체가 어깨를 축으로 좌우로 흔들린다(`figures.css` fig-arm-sway · 토큰 `--dur-pulse`) */
  moving?: boolean
  /**
   * 쥔 트레이를 **늘 수평으로**(웨이퍼 핸들러처럼) 그린다 — 손목 묶음 안이 아니라 바깥 좌표에서 그리퍼 끝 자리에(순기구학)
   * (eric 2026-10-08 「웨이퍼 넣고 빼듯이.. 트레이는 수평으로 빼고 넣어야 돼」). 6축 가로 라인이 켠다
   */
  heldLevel?: boolean
  /** 트레이를 모듈에 «넣는 중»(`in`) · «빼는 중»(`out`) — 수평으로 미끄러지는 움직임(`figures.css` fig-held-in/out · 토큰 `--dur-pulse`) */
  heldMotion?: 'in' | 'out' | undefined
}

const ELBOW_X = SHOULDER_X + L1
const WRIST_X = ELBOW_X + L2
const TIP_X = WRIST_X + L3

/**
 * 팔 마디 하나 — 두 점을 잇는 «두께 w» 의 막대.
 * 🔴 **콘솔은 굵은 선이 아니라 본선 테의 둥근 막대(면 채움)**다(2026-10-11 · eric 「라인 굵기는 다 통일」 · 팀장 결정 — 6축 팔도 예외 없이 본선,
 *    두께는 모양으로). 굵기가 다른 선(7 · 5)으로 「팔은 두께가 있는 물건」을 말하던 것을 막대 폭이 말한다. 클래스 `fig-arm__link` 의 테가
 *    선 굵기 두 단의 본선(`figures.css`)이다.
 * ⚠ 콘솔 스킨이 아닌 앱(장비 관점 · 모듈 패드)은 종전 굵은 선 그대로다.
 */
function ArmLink({
  x1,
  y1,
  x2,
  y2,
  w,
  bar,
}: {
  x1: number
  y1: number
  x2: number
  y2: number
  w: number
  bar: boolean
}) {
  if (!bar) {
    return <line strokeWidth={w === 7 ? undefined : w} x1={x1} x2={x2} y1={y1} y2={y2} />
  }
  const len = Math.hypot(x2 - x1, y2 - y1)
  const deg = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI
  return (
    <rect
      className="fig-arm__link"
      height={w}
      rx={w / 2}
      transform={`rotate(${String(deg)} ${String(x1)} ${String(y1)})`}
      width={len + w}
      x={x1 - w / 2}
      y={y1 - w / 2}
    />
  )
}

/** 마디 하나의 회전 — 제 관절점을 축으로(`transform-box: view-box` · `figures.css`) */
function segmentStyle(deg: number, originX: number, originY: number): CSSProperties {
  return {
    transform: `rotate(${String(deg)}deg)`,
    transformOrigin: `${String(originX)}px ${String(originY)}px`,
  }
}

export function ArmFigure({
  joints,
  holding = false,
  jointLabels = true,
  label,
  moving = false,
  heldLevel = false,
  heldMotion,
}: ArmFigureProps) {
  const j2 = joints[1] ?? 0
  const j3 = joints[2] ?? 0
  const j4 = joints[3] ?? 0
  const spread = holding ? 7 : 14
  const tip = heldLevel ? gripperTip(joints) : undefined
  /* 콘솔이면 마디를 본선 테의 막대로(`ArmLink`) */
  const bar = hasSkin()

  /* 그리퍼는 손목 마디 안에서 +x 방향으로 뻗는다 — 마디가 돌면 같이 돈다 */
  const finger = (sign: number) => (
    <ArmLink
      bar={bar}
      key={sign}
      w={5}
      x1={TIP_X}
      x2={TIP_X + 18}
      y1={SHOULDER_Y + sign * spread}
      y2={SHOULDER_Y + sign * spread}
    />
  )

  return (
    <svg
      aria-label={label}
      className="fig fig-arm"
      data-moving={moving ? 'true' : undefined}
      role="img"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={7}
      viewBox="0 0 240 200"
      width={200}
    >
      <rect className="fig-arm__solid" height={14} rx={3} width={80} x={BASE_X - 40} y={BASE_Y} />
      <ArmLink bar={bar} w={7} x1={BASE_X} x2={SHOULDER_X} y1={BASE_Y} y2={SHOULDER_Y} />
      {/* 어깨부터 그리퍼까지 — 이동 중이면 이 묶음이 어깨를 축으로 흔들린다 */}
      <g
        className={moving ? 'fig-arm__chain fig-arm__chain--moving' : 'fig-arm__chain'}
        data-part="chain"
      >
        <g
          className="fig-arm__seg"
          data-joint="J2"
          style={segmentStyle(j2, SHOULDER_X, SHOULDER_Y)}
        >
          <ArmLink bar={bar} w={7} x1={SHOULDER_X} x2={ELBOW_X} y1={SHOULDER_Y} y2={SHOULDER_Y} />
          <circle className="fig-arm__joint" cx={SHOULDER_X} cy={SHOULDER_Y} r={12} />
          <g className="fig-arm__seg" data-joint="J3" style={segmentStyle(j3, ELBOW_X, SHOULDER_Y)}>
            <ArmLink bar={bar} w={7} x1={ELBOW_X} x2={WRIST_X} y1={SHOULDER_Y} y2={SHOULDER_Y} />
            <circle className="fig-arm__joint" cx={ELBOW_X} cy={SHOULDER_Y} r={11} />
            <g
              className="fig-arm__seg"
              data-joint="J4"
              style={segmentStyle(j4 + 180, WRIST_X, SHOULDER_Y)}
            >
              <ArmLink bar={bar} w={5} x1={WRIST_X} x2={TIP_X} y1={SHOULDER_Y} y2={SHOULDER_Y} />
              <circle className="fig-arm__joint" cx={WRIST_X} cy={SHOULDER_Y} r={8} />
              <ArmLink
                bar={bar}
                w={5}
                x1={TIP_X}
                x2={TIP_X}
                y1={SHOULDER_Y - spread}
                y2={SHOULDER_Y + spread}
              />
              {finger(1)}
              {finger(-1)}
              {holding && tip === undefined ? (
                /*
                 * 쥔 것 — **트레이**(6축이 나르는 것은 챔버가 아니라 트레이다 · eric 2026-10-08 「트레이를 넣고 빼는 거 아니야? 지금은 챔버를
                 * 들고 있는데?」). 그리퍼가 판의 가장자리를 물고 있어 손가락 사이 두께만큼 얇고 바깥으로 길게 뻗은 판으로 그린다
                 */
                <rect
                  className="fig-arm__solid"
                  data-part="held"
                  height={6}
                  rx={1}
                  width={36}
                  x={TIP_X + 10}
                  y={SHOULDER_Y - 3}
                />
              ) : null}
            </g>
          </g>
        </g>
      </g>
      {holding && tip !== undefined ? (
        /*
         * 수평 트레이 — 손목이 어떻게 돌아도 판은 늘 수평(웨이퍼 핸들러 · eric 2026-10-08). 바깥 좌표라 마디 회전을 안 탄다 · 끝 자리는 순기구학.
         * 넣는 중 · 빼는 중이면 수평으로 미끄러진다(CSS · 토큰 시간)
         */
        <g
          className={cx(
            'fig-arm__held',
            heldMotion === 'in' && 'fig-arm__held--in',
            heldMotion === 'out' && 'fig-arm__held--out',
          )}
          data-motion={heldMotion}
          data-part="held-level"
          style={{ transform: `translate(${String(tip.x)}px, ${String(tip.y)}px)` }}
        >
          <rect
            className="fig-arm__solid"
            data-part="held"
            height={6}
            rx={1}
            width={44}
            x={-22}
            y={-3}
          />
        </g>
      ) : null}
      {jointLabels ? (
        <>
          <text className="fig-arm__lbl" x={BASE_X + 46} y={BASE_Y + 11}>
            J1
          </text>
          <text className="fig-arm__lbl" textAnchor="end" x={SHOULDER_X - 17} y={SHOULDER_Y + 4}>
            J2
          </text>
        </>
      ) : null}
    </svg>
  )
}
